import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataState,
  InfoRow,
  PageHeader,
  Select,
  StatCard,
  useToast,
} from "@vms/ui-web";
import { useApi, useCanIDo } from "../../auth/AuthProvider";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string | null;
  phoneNumber?: string | null;
  companyName?: string | null;
  notes?: string | null;
  venueId?: string | null;
  anonymisedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DriverDetailPayload {
  driver: Driver;
  stats: {
    totalSessions: number;
    fastestLapMs: number | null;
    lastSessionAt: string | null;
  };
  recentSessions: Array<{
    id: string;
    venueId: string;
    venueSlug: string | null;
    venueName: string | null;
    eventId: string;
    eventName: string | null;
    car?: string | null;
    carName?: string | null;
    track?: string | null;
    trackName?: string | null;
    status: string;
    createdAt: string;
  }>;
}

export function DriverDetail() {
  const { driverId } = useParams<{ driverId: string }>();
  const { currentOrg, venues } = useOrgContext();
  const api = useApi();
  const canIDo = useCanIDo();
  const { addToast } = useToast();

  const detail = useQuery<DriverDetailPayload>(
    currentOrg && driverId ? `/api/organisations/${currentOrg.id}/drivers/${driverId}` : null,
  );

  const [busy, setBusy] = useState<"anonymise" | "lock-venue" | "unlock" | null>(null);
  const [confirmAnonymise, setConfirmAnonymise] = useState(false);
  const [lockVenueId, setLockVenueId] = useState<string>("");

  if (!currentOrg) return null;
  const venueLookup = new Map(venues.map((v) => [v.id, v.name]));

  async function patch(action: "anonymise" | "lock-venue" | "unlock", venueId?: string) {
    if (!currentOrg || !driverId) return;
    setBusy(action);
    try {
      const res = await api(`/api/organisations/${currentOrg.id}/drivers/${driverId}`, {
        method: "PATCH",
        body: JSON.stringify({ action, venueId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? `Failed to ${action}`, "error");
        return;
      }
      addToast(
        action === "anonymise"
          ? "Driver anonymised"
          : action === "lock-venue"
            ? "Driver locked to venue"
            : "Driver unlocked",
      );
      detail.refetch();
    } finally {
      setBusy(null);
      setConfirmAnonymise(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={detail.data?.driver.displayName ?? "Driver"}
        subtitle={
          <Link
            to={`/org/${currentOrg.slug}/drivers`}
            className="text-primary hover:underline"
          >
            ← All drivers
          </Link>
        }
      />

      <DataState loading={detail.loading} error={detail.error}>
        {detail.data && (
          <>
            <Card className="p-5">
              <div className="flex items-start justify-between gap-6">
                <div className="space-y-1">
                  <div className="text-lg font-semibold text-gray-900">
                    {detail.data.driver.displayName}
                  </div>
                  {detail.data.driver.anonymisedAt && (
                    <Badge className="bg-gray-100 text-gray-700">Anonymised</Badge>
                  )}
                  {detail.data.driver.venueId && (
                    <Badge className="bg-blue-100 text-blue-700">
                      Venue-locked: {venueLookup.get(detail.data.driver.venueId) ?? detail.data.driver.venueId}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {canIDo("drivers:edit", currentOrg.id) && !detail.data.driver.anonymisedAt && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setConfirmAnonymise(true)}
                      loading={busy === "anonymise"}
                    >
                      Anonymise (GDPR)
                    </Button>
                  )}
                  {canIDo("drivers:lock-to-venue", currentOrg.id) &&
                    !detail.data.driver.anonymisedAt &&
                    !detail.data.driver.venueId && (
                      <div className="flex items-center gap-2">
                        <Select
                          compact
                          value={lockVenueId}
                          onChange={(e) => setLockVenueId(e.target.value)}
                          options={[
                            { value: "", label: "Lock to…" },
                            ...venues.map((v) => ({ value: v.id, label: v.name })),
                          ]}
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!lockVenueId}
                          loading={busy === "lock-venue"}
                          onClick={() => patch("lock-venue", lockVenueId)}
                        >
                          Lock
                        </Button>
                      </div>
                    )}
                  {canIDo("drivers:lock-to-venue", currentOrg.id) &&
                    detail.data.driver.venueId && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={busy === "unlock"}
                        onClick={() => patch("unlock")}
                      >
                        Unlock from venue
                      </Button>
                    )}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoRow label="Email" value={detail.data.driver.email || "—"} />
                <InfoRow label="Phone" value={detail.data.driver.phoneNumber || "—"} />
                <InfoRow label="Company" value={detail.data.driver.companyName || "—"} />
                <InfoRow
                  label="Created"
                  value={new Date(detail.data.driver.createdAt).toLocaleString()}
                />
              </div>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatCard label="Total sessions" value={detail.data.stats.totalSessions} />
              <StatCard
                label="Fastest lap"
                value={
                  detail.data.stats.fastestLapMs != null
                    ? formatLap(detail.data.stats.fastestLapMs)
                    : "—"
                }
              />
              <StatCard
                label="Last visit"
                value={
                  detail.data.stats.lastSessionAt
                    ? formatRelative(detail.data.stats.lastSessionAt)
                    : "—"
                }
              />
            </div>

            <Card className="overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 font-semibold text-gray-900">
                Recent sessions
              </div>
              {detail.data.recentSessions.length === 0 ? (
                <div className="px-5 py-6 text-sm text-gray-500">No sessions yet.</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {detail.data.recentSessions.map((s) => {
                    const vslug = s.venueSlug;
                    const sessionHref = vslug
                      ? `/org/${currentOrg.slug}/venue/${vslug}/sessions/${s.id}`
                      : null;
                    const eventHref = vslug
                      ? `/org/${currentOrg.slug}/venue/${vslug}/events/${s.eventId}`
                      : null;
                    return (
                      <li
                        key={s.id}
                        className="px-5 py-3 text-sm flex items-center justify-between gap-4 hover:bg-gray-50"
                      >
                        <div className="min-w-0 flex-1">
                          {sessionHref ? (
                            <Link
                              to={sessionHref}
                              className="font-medium text-indigo-600 hover:text-indigo-800 block truncate"
                            >
                              {s.carName ?? s.car ?? "—"} · {s.trackName ?? s.track ?? "—"}
                            </Link>
                          ) : (
                            <div className="font-medium text-gray-900 truncate">
                              {s.carName ?? s.car ?? "—"} · {s.trackName ?? s.track ?? "—"}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-0.5">
                            {new Date(s.createdAt).toLocaleString()} ·{" "}
                            {s.venueName ?? venueLookup.get(s.venueId) ?? s.venueId}
                            {eventHref && s.eventName ? (
                              <>
                                {" · "}
                                <Link
                                  to={eventHref}
                                  className="text-primary hover:underline"
                                >
                                  {s.eventName}
                                </Link>
                              </>
                            ) : null}
                          </div>
                        </div>
                        <Badge
                          className={
                            s.status === "completed"
                              ? "bg-green-100 text-green-700"
                              : s.status === "cancelled"
                                ? "bg-gray-100 text-gray-700"
                                : "bg-blue-100 text-blue-700"
                          }
                        >
                          {s.status}
                        </Badge>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </>
        )}
      </DataState>

      <ConfirmDialog
        isOpen={confirmAnonymise}
        title="Anonymise driver?"
        message="This irreversibly clears the driver's name, email, phone number and notes — used to satisfy a GDPR erasure request. Sessions and lap times remain attached to the (now anonymous) driver row."
        confirmLabel="Anonymise"
        loading={busy === "anonymise"}
        onClose={() => setConfirmAnonymise(false)}
        onConfirm={() => patch("anonymise")}
      />
    </div>
  );
}

function formatLap(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = ((ms % 60000) / 1000).toFixed(3);
  return `${minutes}:${seconds.padStart(6, "0")}`;
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  const days = Math.floor(seconds / 86400);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
